import React from 'react';
import StackedListWrapper from '../StackedListWrapper/StackedListWrapper';
import StackedListItem from '../StackedListItem/StackedListItem';
import StackedListSkeleton from '../StackedListSkeleton/StackedListSkeleton';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export default function StackedList({
  items = [],
  settings,
  onAction,
  pagination,
  loading,
  itemComponent,
  emptyMessage,
  getIsItemUpdating,
}) {
  const ItemComponent = itemComponent || StackedListItem;
  // If pagination is provided, we assume 'items' are the current page's items.
  // pagination prop: { pageIndex, pageSize, totalItems, onPageChange }

  const renderPagination = () => {
    if (!pagination) return null;

    const { pageIndex, pageSize, totalItems, onPageChange } = pagination;
    const start = pageIndex * pageSize + 1;
    const end = Math.min((pageIndex + 1) * pageSize, totalItems);
    const canPrevious = pageIndex > 0;
    const canNext = (pageIndex + 1) * pageSize < totalItems;

    return (
      <div className="flex items-center justify-between border-t border-gray-700 bg-gray-900 px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPrevious}
            className="relative inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNext}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-400">
              Showing <span className="font-medium text-white">{start}</span> to <span className="font-medium text-white">{end}</span> of{' '}
              <span className="font-medium text-white">{totalItems}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(pageIndex - 1)}
                disabled={!canPrevious}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPageChange(pageIndex + 1)}
                disabled={!canNext}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <StackedListWrapper>
      {loading ? (
        <StackedListSkeleton />
      ) : items.length === 0 && emptyMessage ? (
        <div className="px-4 py-10 text-center text-sm text-gray-400">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <>
          <ul role="list" className="divide-y divide-gray-800">
            {items.map((item) => (
              <ItemComponent
                key={item._id}
                item={item}
                settings={settings}
                onAction={onAction}
                isUpdating={getIsItemUpdating ? getIsItemUpdating(item) : false}
              />
            ))}
          </ul>
          {renderPagination()}
        </>
      )}
    </StackedListWrapper>
  );
}
